import React, { useState, useEffect } from 'react';
import { Play, Pause, Youtube } from 'lucide-react';
import { Button } from './ui/button';
import { useAudioPlayer } from '../context/AudioPlayerContext';

export const FeaturedTrack = () => {
  const [featuredTrack, setFeaturedTrack] = useState(null);
  const { currentTrack, isPlaying, playTrack } = useAudioPlayer();
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchFeaturedTrack();

    // Re-fetch periodically so scheduled tracks appear live without a page reload.
    // 60s is a good compromise: feels near-real-time on release without hammering the API.
    const interval = setInterval(fetchFeaturedTrack, 60_000);

    // Also re-fetch the moment the tab becomes visible again
    // (catches laptops that were asleep at release time).
    const onVisible = () => {
      if (document.visibilityState === 'visible') fetchFeaturedTrack();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  const fetchFeaturedTrack = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/tracks/featured`, { cache: 'no-store' });
      if (response.ok) {
        const data = await response.json();
        setFeaturedTrack(data);
      } else {
        // 404 means no featured-and-published track right now — clear any stale state
        setFeaturedTrack(null);
      }
    } catch (error) {
      // network error — keep whatever we have rather than blanking the page
    }
  };

  const handlePlay = () => {
    if (featuredTrack) {
      playTrack(featuredTrack);
    }
  };

  if (!featuredTrack) {
    return null;
  }

  const isFeaturedPlaying = currentTrack?._id === featuredTrack._id && isPlaying;

  return (
    <section className="relative py-20 px-4 bg-gradient-to-b from-purple-950/30 via-black to-black overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-r from-purple-500/10 to-pink-500/10"></div>
      
      <div className="relative z-10 max-w-6xl mx-auto">
        <div className="text-center mb-8">
          <span className="inline-block px-4 py-2 bg-purple-600/20 border border-purple-500/30 rounded-full text-purple-300 text-sm font-medium mb-4">
            Featured Track
          </span>
          <h2 className="text-5xl md:text-6xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent mb-4">
            {featuredTrack.title}
          </h2>
        </div>

        <div className="flex flex-col md:flex-row items-center justify-center gap-8 max-w-4xl mx-auto">
          {/* Play Button */}
          {featuredTrack.audio_url && (
            <Button
              onClick={handlePlay}
              size="lg"
              className="h-24 w-24 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 shadow-lg shadow-purple-500/50 transition-all duration-300 hover:scale-110"
            >
              {isFeaturedPlaying ? (
                <Pause className="h-10 w-10" />
              ) : (
                <Play className="h-10 w-10 ml-1" />
              )}
            </Button>
          )}

          {/* Track Info */}
          <div className="flex-1 text-center md:text-left">
            <p className="text-purple-200 text-lg mb-4">
              Latest Release{isFeaturedPlaying ? ' - Now Playing' : ''}
            </p>
            <div className="flex flex-wrap gap-3 justify-center md:justify-start">
              {featuredTrack.youtube_url && (
                <Button
                  asChild
                  variant="outline"
                  className="border-purple-500 text-purple-300 hover:bg-purple-500/20"
                >
                  <a href={featuredTrack.youtube_url} target="_blank" rel="noopener noreferrer">
                    <Youtube className="mr-2 h-4 w-4" />
                    Watch on YouTube
                  </a>
                </Button>
              )}
            </div>
          </div>

          {/* Visual Wave */}
          <div className="flex items-center gap-1 h-24">
            {[...Array(20)].map((_, i) => (
              <div
                key={`wave-${i}`}
                className={`w-1 bg-gradient-to-t from-purple-600 to-pink-600 rounded-full transition-all duration-300 ${
                  isFeaturedPlaying ? 'animate-pulse' : ''
                }`}
                style={{
                  height: `${Math.random() * 80 + 20}%`,
                  animationDelay: `${i * 0.1}s`
                }}
              ></div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};